// The desk palette encodes capacity and orientation in a single id:
//
//   odd  id -> horizontal, even id -> vertical
//   capacity = ceil(id / 2)
//
// so 1 = single horizontal, 2 = single vertical, 3 = 2-seat horizontal, and so
// on up to 12. Both directions of that mapping live here; they used to be
// open-coded in six places, which is how the grid and the loader could disagree
// about what a saved table looks like.

const MAX_TYPE_ID = 12;

/** A cell draws at most two desks, of at most six seats each. */
export const MAX_TABLES_PER_CELL = 2;
export const MAX_TABLE_CAPACITY = 6;

export const HORIZONTAL = "HORIZONTAL";
export const VERTICAL = "VERTICAL";

export const capacityOfType = (typeId) => Math.ceil((Number(typeId) || 1) / 2);

export const isHorizontalType = (typeId) => (Number(typeId) || 1) % 2 === 1;

export const orientationOfType = (typeId) =>
  isHorizontalType(typeId) ? HORIZONTAL : VERTICAL;

export const typeIdOf = (capacity, horizontal) => {
  const seats = Math.max(1, Number(capacity) || 1);
  return horizontal ? seats * 2 - 1 : seats * 2;
};

/** The palette id that matches a saved `table` row. */
export const typeIdOfTable = (table) =>
  String(
    typeIdOf(
      Number(table?.capacity) || 0,
      String(table?.orientation || "").toUpperCase() === HORIZONTAL,
    ),
  );

export const nameOfType = (typeId) => {
  const seats = capacityOfType(typeId);
  const count = seats === 1 ? "Single" : `${seats}x`;
  const orientation = isHorizontalType(typeId) ? "Horizontal" : "Vertical";
  return `${count} ${orientation} Table${seats > 1 ? "s" : ""}`;
};

export const iconSrcOfType = (typeId) => {
  const base = process.env.PUBLIC_URL || "";
  const orientation = isHorizontalType(typeId) ? "hor" : "ver";
  return `${base}/layoutIcons/${capacityOfType(typeId)}${orientation}.jpg`;
};

/**
 * A room layout's total desk seats, as the desks the grid can actually draw.
 *
 * A template describes seats, not furniture the grid understands: "open plan,
 * 4 desks" is one room's worth of seating, and the grid has room for two desk
 * icons of up to six seats. So the seats are packed rather than turned into one
 * table per template item — which produced four capacity-1 tables of which the
 * grid then drew only the first two.
 *
 *    2 seats  -> [2]      one 2-seat desk
 *    4 seats  -> [4]      one 4-seat desk
 *    8 seats  -> [4, 4]
 *   12 seats  -> [6, 6]
 *
 * Twelve is the most a cell can show; anything beyond that is clamped, because
 * there is no icon for it.
 */
export function packSeatsIntoTables(totalSeats) {
  const seats = Math.max(0, Math.trunc(Number(totalSeats) || 0));
  if (seats === 0) return [];
  if (seats <= MAX_TABLE_CAPACITY) return [seats];

  const first = Math.min(
    Math.ceil(seats / MAX_TABLES_PER_CELL),
    MAX_TABLE_CAPACITY,
  );
  const second = Math.min(seats - first, MAX_TABLE_CAPACITY);
  return second > 0 ? [first, second] : [first];
}

/** The palette, in id order. */
export const TABLE_TYPES = Array.from({ length: MAX_TYPE_ID }, (_, index) => {
  const id = index + 1;
  return { id, name: nameOfType(id) };
});
